import { useCallback, useContext, useEffect, useMemo, useState } from "react";

import { debounce } from "@mui/material";

import { RootContext } from "../context/RootContext";
import { LoansContext } from "../context/LoansContext";

import {
  getAutocompleteAddress,
  getBorrowerDetailsBasedOnEmail,
  getGuarantorBasedOnBorrowerId,
  getZipCodeList,
} from "../services/network";

import { ZipCodeSuggestion } from "../types/fieldTypes";
import { ObjectType } from "../types/tableTypes";
import { getAutoCalculatedResults } from "../utils/Common";

interface Field {
  id: string;
  getFieldErrors?: (value: any) => string;
  [key: string]: any;
}

interface Unit {
  [key: string]: any;
}

interface Property {
  propertyLocation?: any;
  propertyInformation?: any;
  propertyEconomics?: any;
  unitInformation?: Unit[];
  [key: string]: any;
}

interface DetailedLoanSizingData {
  propertyDetails?: Property[];
  [key: string]: any;
}

// Constants
const PROPERTY_LEVEL_SECTIONS = [
  "propertyLocation",
  "propertyInformation",
  "propertyEconomics",
  "unitInformation",
] as const;

const PROPERTY_LOCATION_FIELDS = ["postalCode", "address"] as const;

const SPECIAL_FIELD_UPDATES = {
  interestOnlyFlag: (value: string, id?: number) => ({
    ioPeriod: value === "Yes" ? "120" : null,
  }),
  prepaymentPenaltyTypeMonths: (value: any, id?: number) => ({
    prepayPenaltyType: value === 0 ? 0 : null,
  }),
  borrowerType: (id?: number) => ({
    firstTimeHomeBuyer: null,
  }),
  numberOfProperties: (value: any, id?: number) => ({
    crossCollaterlization: value > 1 ? "Y" : "N",
  }),
  loanPurpose: (value: string, id?: number) => ({
    cashOutFlag: value === "Purchase" ? "No Cash Out" : "No Cash",
  }),
};

export const useLoanSizingHandler = () => {
  const { dynamicConfig } = useContext(RootContext);
  const {
    detailedLoanSizingData,
    setDetailedLoanSizingData,
    setNewDebouncedValue,
    detailedLoanSizingErrors,
    setDetailedLoanSizingErrors,
    addressOptions,
    setAddressOptions,
    setZipCodeOptions,
    setBorrowerOptions,
    setGuarantorOptions,
    guarantorOptions,
    borrowerOptions,
    loanDetails,
    updateDetailedLoanSizingData,
  } = useContext(LoansContext);

  const [zipCodeInputValue, setZipCodeInputValue] = useState<string>("");
  const [addressInputValue, setAddressInputValue] = useState<string>("");
  const [borrowerNameInputValue, setBorrowerNameInputValue] =
    useState<string>("");
  const [guarantorNameInputValue, setGuarantorNameInputValue] =
    useState<string>("");

  const fetchAddress = useCallback(
    debounce(async (request: { input: string }) => {
      try {
        const { data } = await getAutocompleteAddress(
          dynamicConfig?.ViteAppSmartyStreetsApiKey,
          request.input,
        );
        setAddressOptions([...data.suggestions]);
      } catch (err) {
        console.error(err);
      }
    }, 300),
    [dynamicConfig?.ViteAppSmartyStreetsApiKey, setAddressOptions],
  );

  const fetchZipCodeSuggestions = useCallback(
    debounce(async (request: { input: string }) => {
      try {
        const { data } = await getZipCodeList(
          dynamicConfig?.ViteAppSmartyStreetsApiKey,
          request.input,
        );
        const { city_states, zipcodes } = data[0];
        let newOptions = [] as ZipCodeSuggestion[];
        if (city_states && zipcodes) {
          const zipCode = zipcodes[0].zipcode;
          const temp = city_states.map((ele: ObjectType) => ({
            ...ele,
            zipCode,
          }));
          newOptions = [...newOptions, ...temp];
        }
        setZipCodeOptions(newOptions);
      } catch (err) {
        console.error(err);
      }
    }, 200),
    [dynamicConfig?.ViteAppSmartyStreetsApiKey, setZipCodeOptions],
  );

  const updatePostalOrAddress = useCallback(
    (
      value: any,
      propertyId: number,
      section: string,
      id: string,
      isPostalCode?: boolean,
    ) => {
      let tempPropertyDetails = [
        ...(detailedLoanSizingData?.propertyDetails || []),
      ];
      const { street_line, city, state, zipcode } = value || {};
      setAddressOptions(value ? [value, ...addressOptions] : addressOptions);

      let objToReturn: any = {};
      if (isPostalCode) {
        setZipCodeInputValue(value);
        objToReturn = {
          [id]: value?.zipCode || value,
          city: value?.city || "",
          state: value?.state_abbreviation || "",
        };
      } else {
        setAddressInputValue(value);
        objToReturn = {
          address:
            street_line && city && state
              ? `${street_line} ${city} ${state}`
              : value,
          city: city || "",
          state: state || "",
          postalCode: zipcode || "",
        };
      }

      if (propertyId >= 0 && propertyId < tempPropertyDetails.length) {
        tempPropertyDetails = tempPropertyDetails.map(
          (property: any, index: number) => {
            if (index === propertyId) {
              return {
                ...property,
                [section]: {
                  ...property[section],
                  ...objToReturn,
                },
              };
            }
            return property;
          },
        );
      }

      setDetailedLoanSizingData({
        ...detailedLoanSizingData,
        propertyDetails: tempPropertyDetails,
      });
    },
    [
      detailedLoanSizingData,
      addressOptions,
      setAddressOptions,
      setDetailedLoanSizingData,
    ],
  );

  const borrowerFieldMap: Record<string, (value: any) => Record<string, any>> =
    {
      borrowingEntityName: (value) => ({
        borrowingEntityName: value?.fullName,
        borrowerEmail: value?.email,
        id: value?.borrowerId,
      }),
      borrowerName: (value) => ({
        borrowerName:
          `${value?.firstName || ""} ${value?.lastName || ""}`.trim(),
        firstName: value?.firstName,
        lastName: value?.lastName,
        borrowerEmail: value?.email,
        id: value?.borrowerId,
      }),
      guarantorName: (value) => ({
        guarantorName:
          `${value?.firstName || ""} ${value?.lastName || ""}`.trim(),
        firstName: value?.firstName,
        lastName: value?.lastName,
        guarantorEmail: value?.email ?? null,
        guarantorId: value?.borrowerId ?? null,
      }),
      borrowerType: (value) => ({
        ["borrowerType"]: value,
        guarantorName: null,
        firstName: null,
        lastName: null,
        borrowerEmail: null,
        borrowingEntityName: null,
        guarantorEmail: null,
        id: null,
        guarantorId: null,
        borrowerName: null,
      }),
    };

  const handleChange = useCallback(
    (
      value: any,
      type: string,
      field: Field,
      section: string,
      propertyId: number,
      unitId: number,
    ) => {
      const { id } = field;

      if (PROPERTY_LOCATION_FIELDS.includes(id as any)) {
        updatePostalOrAddress(
          value,
          propertyId,
          section,
          id,
          id === "postalCode",
        );
      } else if (
        PROPERTY_LEVEL_SECTIONS.includes(
          section as (typeof PROPERTY_LEVEL_SECTIONS)[number],
        )
      ) {
        let tempPropertyDetails = [
          ...(detailedLoanSizingData?.propertyDetails || []),
        ];

        if (propertyId >= 0 && propertyId < tempPropertyDetails.length) {
          tempPropertyDetails = tempPropertyDetails.map(
            (property: any, index: number) => {
              if (index === propertyId) {
                if (unitId >= 0 && unitId < property?.unitInformation?.length) {
                  const currentUnitInfo = property[section]?.map(
                    (unit: any, uId: number) =>
                      uId === unitId ? { ...unit, [id]: value } : unit,
                  );
                  return {
                    ...property,
                    [section]: currentUnitInfo,
                  };
                }
                if (
                  section === "propertyEconomics" &&
                  [
                    "borrowerPaidCostToDate",
                    "purchasePrice",
                    "closingCosts",
                  ]?.includes(id)
                ) {
                  return {
                    ...property,
                    [section]: {
                      ...property[section],
                      [id]: value,
                      ["costBasis"]: getAutoCalculatedResults(
                        "costBasis",
                        {
                          ...property,
                          [section]: { ...property[section], [id]: value },
                        },
                        {
                          isMultifamily:
                            loanDetails?.propertyType ===
                            "Multifamily/MixedUse",
                        },
                      ),
                      ["costBasisCalculated"]: id === "purchasePrice" ? +(value || 0) + +(property?.propertyEconomics?.verifiedCostsAndImprovements || 0) : property?.propertyEconomics?.costBasisCalculated
                    },
                  };
                }
                if (section === "propertyEconomics" && ["verifiedCostsAndImprovements"]?.includes(id)) {
                  return {
                    ...property,
                    [section]: {
                      ...property[section],
                      [id]: value,
                      ["costBasisCalculated"]: +(property?.propertyEconomics?.propertyAcquisitionPrice || property?.propertyEconomics?.purchasePrice || 0) + +(value || 0)
                    },
                  };
                }
                return {
                  ...property,
                  [section]: {
                    ...property[section],
                    [id]: value,
                  },
                };
              }
              return property;
            },
          );
        }

        setDetailedLoanSizingData({
          ...detailedLoanSizingData,
          propertyDetails: tempPropertyDetails,
        });
      } else {
        let returnObj = {
          ...detailedLoanSizingData,
          [section]: {
            ...detailedLoanSizingData?.[section],
            [id]: value,
          },
        };

        if (borrowerFieldMap[id]) {
          returnObj = {
            ...detailedLoanSizingData,
            [section]: {
              ...detailedLoanSizingData?.[section],
              ...borrowerFieldMap[id](value), // spreads the mapped fields
            },
          };
          if (id === "borrowerType") {
            setBorrowerOptions([]);
            setGuarantorOptions([]);
            setBorrowerNameInputValue("");
            setGuarantorNameInputValue("");
          }
        }
        const specialUpdate =
          SPECIAL_FIELD_UPDATES[id as keyof typeof SPECIAL_FIELD_UPDATES];
        if (specialUpdate) {
          returnObj = {
            ...returnObj,
            [section]: {
              ...returnObj[section],
              ...specialUpdate(value, propertyId),
            },
          };
        }
        if (id === "borrowingEntityName") {
          getGuarantorDetails(
            value?.borrowerId,
            dynamicConfig?.fileUploadUrl,
            returnObj,
          );
        } else {
          setDetailedLoanSizingData(returnObj);
        }
      }

      if (["dropDown", "autoComplete"].includes(type)) {
        onBlur(value, type, field, section, propertyId, unitId);
      }
      setNewDebouncedValue(value);
    },
    [
      detailedLoanSizingData,
      setDetailedLoanSizingData,
      updatePostalOrAddress,
      setNewDebouncedValue,
    ],
  );

  const onBlur = useCallback(
    (
      value: any,
      type: string,
      field: Field,
      section: string,
      propertyId: number,
      unitId: number,
    ) => {
      const errorMessage: any = field?.getFieldErrors?.(value);
      const { id } = field;

      if (
        PROPERTY_LOCATION_FIELDS.includes(
          id as (typeof PROPERTY_LOCATION_FIELDS)[number],
        )
      ) {
        updatePostalOrAddress(
          value,
          propertyId,
          section,
          id,
          id === "postalCode",
        );
      } else if (
        PROPERTY_LEVEL_SECTIONS.includes(
          section as (typeof PROPERTY_LEVEL_SECTIONS)[number],
        )
      ) {
        let tempPropertyDetails = [
          ...(detailedLoanSizingErrors?.propertyDetails || [{}]),
        ];

        if (propertyId >= 0 && propertyId < tempPropertyDetails.length) {
          tempPropertyDetails = tempPropertyDetails.map(
            (property: any, index: number) => {
              if (index === propertyId) {
                if (unitId >= 0 && unitId < property?.unitInformation?.length) {
                  const currentUnitInfo = property[section]?.map(
                    (unit: any, uId: number) =>
                      uId === unitId ? { ...unit, [id]: errorMessage } : unit,
                  );
                  return {
                    ...property,
                    [section]: currentUnitInfo,
                  };
                }
                return {
                  ...property,
                  [section]: {
                    ...property[section],
                    [id]: errorMessage,
                  },
                };
              }
              return property;
            },
          );
        }

        setDetailedLoanSizingErrors({
          ...detailedLoanSizingErrors,
          propertyDetails: tempPropertyDetails,
        });
      } else {
        setDetailedLoanSizingErrors({
          ...detailedLoanSizingErrors,
          [section]: {
            ...detailedLoanSizingErrors?.[section],
            [id]: errorMessage,
          },
        });
      }
    },
    [
      detailedLoanSizingErrors,
      updatePostalOrAddress,
      setDetailedLoanSizingErrors,
    ],
  );

  const debouncedBorrowerFetch = useMemo(
    () => debounce((borrowerObj: any) => getBorrowers(borrowerObj), 3000),
    [],
  );

  async function getBorrowers(borrowerObj: any) {
    try {
      if (!borrowerObj.username || !borrowerObj?.borrowerType) return;
      const payload = {
        username: borrowerObj.username,
        tenant: "sizer_to_los",
        borrowerType: borrowerObj?.borrowerType?.toUpperCase(),
        isTestParty: false,
      };
      const data = await getBorrowerDetailsBasedOnEmail(payload);
      if (borrowerObj?.borrowerType === "INDIVIDUAL") {
        setGuarantorOptions(data?.data ?? []);
      } else {
        setBorrowerOptions(data?.data ?? []);
      }
    } catch (err) {
      console.error(err);
    }
  }

  async function getGuarantorDetails(
    borrowerId: string,
    url: string,
    returnObj: any,
  ) {
    try {
      if (!borrowerId) return;
      const { data } = await getGuarantorBasedOnBorrowerId(
        borrowerId,
        url?.split("/bff")[0] ?? "",
      );
      setDetailedLoanSizingData({
        ...detailedLoanSizingData,
        ...returnObj,
        borrowerInformation: {
          ...detailedLoanSizingData?.borrowerInformation,
          ...returnObj.borrowerInformation,
          guarantorName: data?.data?.[0]?.fullName || "",
          guarantorEmail: data?.data?.[0]?.email || "",
          guarantorId: data?.data?.[0]?.borrowerId || "",
        },
      });
    } catch (err) {
      console.error(err);
    }
  }
  useEffect(() => {
    if (zipCodeInputValue.length >= 4 && zipCodeInputValue.length <= 7) {
      fetchZipCodeSuggestions({ input: zipCodeInputValue });
    }
  }, [zipCodeInputValue, fetchZipCodeSuggestions]);

  useEffect(() => {
    if (addressInputValue) {
      fetchAddress({ input: addressInputValue });
    }
  }, [addressInputValue, fetchAddress]);

  useEffect(() => {
    const borrowerType =
      detailedLoanSizingData?.borrowerInformation?.borrowerType;
    if (borrowerNameInputValue) {
      debouncedBorrowerFetch({
        username: borrowerNameInputValue,
        borrowerType: borrowerType,
      });
    }
  }, [borrowerNameInputValue, debouncedBorrowerFetch]);

  useEffect(() => {
    if (guarantorNameInputValue) {
      debouncedBorrowerFetch({
        username: guarantorNameInputValue,
        borrowerType: "INDIVIDUAL",
      });
    }
  }, [guarantorNameInputValue, debouncedBorrowerFetch]);

  return {
    handleChange,
    onBlur,
    updatePostalOrAddress,
    addressOrPostalOnInputChange: (
      value: any,
      type: any,
      field: any,
      section: any,
      propertyId: any,
    ) => {
      const { id } = field;
      let tempPropertyDetails = [
        ...(detailedLoanSizingData?.propertyDetails || []),
      ];

      tempPropertyDetails = tempPropertyDetails.map(
        (property: any, index: number) => {
          if (index === propertyId) {
            return {
              ...property,
              [section]: {
                ...property[section],
                [id]: value,
              },
            };
          }
          return property;
        },
      );

      setDetailedLoanSizingData({
        ...detailedLoanSizingData,
        propertyDetails: tempPropertyDetails,
      });

      if (field.id === "postalCode") {
        setZipCodeInputValue(value);
      } else {
        setAddressInputValue(value);
      }
    },
    borrowerOnInputChange: (value: any, id: string) => {
      // if value is an object (selected option), ignore — handleChange already handled it
      if (typeof value === "object" && value !== null) return;

      const updated = {
        ...detailedLoanSizingData,
        borrowerInformation: {
          ...detailedLoanSizingData?.borrowerInformation,
          [id]: value,
        },
      };

      if (["borrowingEntityName", "borrowerName"].includes(id)) {
        updated.borrowerInformation = {
          ...updated.borrowerInformation,
          id:
            borrowerOptions?.find((g: any) => [value].includes(g.fullName))
              ?.borrowerId ?? null,
        };
      }
      updateDetailedLoanSizingData(updated); // ← syncs ref + state together
      setBorrowerNameInputValue(value);
    },
    guarantorOnInputChange: (value: any, id: string) => {
      // if value is an object (selected option), ignore — handleChange already handled it
      if (typeof value === "object" && value !== null) return;

      const updated = {
        ...detailedLoanSizingData,
        borrowerInformation: {
          ...detailedLoanSizingData?.borrowerInformation,
          [id]: value,
        },
      };

      if (["guarantorEmail", "guarantorName"].includes(id)) {
        updated.borrowerInformation = {
          ...updated.borrowerInformation,
          guarantorId:
            guarantorOptions?.find(
              (g: any) =>
                g.email === updated.borrowerInformation.guarantorEmail &&
                g.fullName === updated.borrowerInformation.guarantorName,
            )?.borrowerId ?? null,
        };
      }
      updateDetailedLoanSizingData(updated); // ← syncs ref + state together
      setGuarantorNameInputValue(value);
    },
  };
};
